
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

type Expression = 'happy' | 'thinking' | 'talking';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  guideElement?: string;
  guideText?: string;
  guideAction?: () => void;
}

interface QuickQuestion {
  id: string;
  icon: string;
  text: string;
  category: string;
}

interface TourStep {
  target: string;
  title: string;
  description: string;
  action?: boolean;
}

interface Position {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TooltipPosition {
  top: number;
  left: number;
}

/* -------------------------------------------------------------------------- */
/*                         DEFINIÇÃO DOS PASSOS DO TOUR                     */
/* -------------------------------------------------------------------------- */
const pixTourSteps: TourStep[] = [
  {
    target: '[data-tour="tab-financeira"]',
    title: 'Categoria Financeira',
    description: 'Primeiro, clique na aba "Financeira" para ver as opções de resgate financeiro.',
    action: true,
  },
  {
    target: '[data-tour="card-pix"]',
    title: 'Card PIX',
    description: 'Este é o card de resgate via PIX. Aqui você pode transferir seus pontos diretamente para sua conta.',
  },
  {
    target: '[data-tour="btn-resgatar-pix"]',
    title: 'Botão Resgatar',
    description: 'Clique no botão "Resgatar Agora" para abrir o formulário de resgate PIX.',
    action: true,
  },
  {
    target: '[data-tour="select-credito"]',
    title: 'Selecionar Crédito',
    description: 'Escolha qual crédito você deseja usar para o resgate. Você pode ver o saldo disponível de cada um.',
  },
  {
    target: '[data-tour="tipo-chave-pix"]',
    title: 'Tipo de Chave PIX',
    description: 'Selecione o tipo da sua chave PIX: CPF, E-mail, Celular ou Chave Aleatória.',
  },
  {
    target: '[data-tour="input-chave-pix"]',
    title: 'Digite a Chave PIX',
    description: 'Digite sua chave PIX conforme o tipo selecionado. Exemplo: para CPF digite apenas números.',
  },
  {
    target: '[data-tour="input-descricao"]',
    title: 'Descrição (Opcional)',
    description: 'Você pode adicionar uma descrição para identificar esta transferência no seu extrato.',
  },
  {
    target: '[data-tour="btn-continuar"]',
    title: 'Continuar',
    description: 'Clique em "Continuar" para revisar todos os dados antes de confirmar o resgate.',
    action: true,
  },
  {
    target: '[data-tour="btn-confirmar-pix"]',
    title: 'Confirmar PIX',
    description:
      'Revise todos os dados e clique em "Confirmar PIX" para finalizar a transferência. O valor será creditado em até 24 horas úteis.',
  },
];

const pagamentoTourSteps: TourStep[] = [
  {
    target: '[data-tour="tab-financeira"]',
    title: 'Categoria Financeira',
    description: 'Primeiro, clique na aba "Financeira" para ver as opções de resgate financeiro.',
    action: true,
  },
  {
    target: '[data-tour="card-pagamento"]',
    title: 'Card Pagamento de Contas',
    description:
      'Este é o card de pagamento de contas. Aqui você pode pagar boletos e contas de consumo usando seus pontos.',
  },
  {
    target: '[data-tour="btn-resgatar-pagamento"]',
    title: 'Botão Resgatar',
    description: 'Clique no botão "Resgatar Agora" para abrir o formulário de pagamento de contas.',
    action: true,
  },
  {
    target: '[data-tour="select-credito"]',
    title: 'Selecionar Crédito',
    description: 'Escolha qual crédito você deseja usar para o pagamento. Você pode ver o saldo disponível de cada um.',
  },
  {
    target: '[data-tour="tipo-pagamento"]',
    title: 'Tipo de Pagamento',
    description:
      'Selecione o tipo de pagamento: Boleto Bancário ou Conta de Consumo (água, luz, telefone, etc).',
  },
  {
    target: '[data-tour="input-codigo-barras"]',
    title: 'Código de Barras',
    description: 'Digite ou cole o código de barras da sua conta. O sistema aceita códigos com ou sem espaços.',
  },
  {
    target: '[data-tour="btn-validar-codigo"]',
    title: 'Validar Código',
    description: 'Clique em "Validar" para o sistema buscar os dados da conta (valor, vencimento, beneficiário).',
    action: true,
  },
  {
    target: '[data-tour="btn-continuar"]',
    title: 'Continuar',
    description:
      'Após validar, clique em "Continuar" para revisar todos os dados antes de confirmar o pagamento.',
    action: true,
  },
  {
    target: '[data-tour="btn-confirmar-pagamento"]',
    title: 'Confirmar Pagamento',
    description:
      'Revise todos os dados e clique em "Confirmar Pagamento" para finalizar. A conta será paga em até 24 horas úteis.',
  },
];

/* -------------------------------------------------------------------------- */
/*                               COMPONENTE PRINCIPAL                        */
/* -------------------------------------------------------------------------- */
export const ChatbotAvatar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [expression, setExpression] = useState<Expression>('happy');
  const [highlightedElement, setHighlightedElement] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text:
        'Olá! 👋 Sou seu assistente virtual da plataforma de incentivos. Como posso ajudar você hoje?',
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // ESTADOS DO TOUR
  const [showTourOverlay, setShowTourOverlay] = useState(false);
  const [currentTourType, setCurrentTourType] = useState<'pix' | 'pagamento' | null>(null);
  const [currentTourStep, setCurrentTourStep] = useState(0);
  const [currentTourSteps, setCurrentTourSteps] = useState<TourStep[]>([]);
  const [highlightPosition, setHighlightPosition] = useState<Position | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition | null>(null);
  const [isTourActive, setIsTourActive] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);

  const quickQuestions: QuickQuestion[] = [
    { id: '1', icon: 'ri-trophy-line', text: 'Como ganhar pontos?', category: 'pontos' },
    { id: '2', icon: 'ri-gift-line', text: 'Como resgatar recompensas?', category: 'recompensas' },
    { id: '3', icon: 'ri-medal-line', text: 'O que são distintivos?', category: 'distintivos' },
    { id: '4', icon: 'ri-flag-line', text: 'Como completar missões?', category: 'missoes' },
    { id: '5', icon: 'ri-bar-chart-line', text: 'Como subir de nível?', category: 'niveis' },
    { id: '6', icon: 'ri-gamepad-line', text: 'Quais jogos disponíveis?', category: 'jogos' },
  ];

  /* ---------------------------------------------------------------------- */
  /*                        FUNÇÕES AUXILIARES                             */
  /* ---------------------------------------------------------------------- */
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  /* ----------  START GUIDED TOUR LISTENER  ---------- */
  useEffect(() => {
    const handleStartTour = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { type } = customEvent.detail;
      startTour(type);
    };
    window.addEventListener('startGuidedTour', handleStartTour);
    return () => {
      window.removeEventListener('startGuidedTour', handleStartTour);
    };
  }, []);

  /* ----------  GUIDE TO ELEMENT (HIGHLIGHT)  ---------- */
  const guideToElement = (elementId: string, _message?: string, action?: () => void) => {
    const element = document.getElementById(elementId) ?? document.querySelector(`[data-guide="${elementId}"]`);
    if (!element) return;

    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedElement(elementId);
    element.classList.add('chatbot-highlight');

    const cleanup = () => {
      element.classList.remove('chatbot-highlight');
      setHighlightedElement(null);
    };
    setTimeout(cleanup, 5000);

    if (action) action();
  };

  const navigateAndHighlight = (path: string, elementId: string) => {
    navigate(path);
    setTimeout(() => guideToElement(elementId), 500);
  };

  /* ----------  CHATBOT RESPONSE LOGIC  ---------- */
  const getResponse = (userMessage: string): Message => {
    const msg = userMessage.toLowerCase();
    const timestamp = new Date();

    // Exemplos de respostas com guia visual – reduzi para clareza
    if (msg.includes('campanha')) {
      return {
        id: Date.now().toString(),
        text:
          '📢 **Campanhas Ativas**\n\nVou te mostrar onde trocar de campanha! 👆\n\n1️⃣ Clique no **seletor de campanhas** no topo da página\n2️⃣ Escolha a campanha desejada\n3️⃣ Veja seus pontos e ranking atualizados',
        sender: 'bot',
        timestamp,
        guideElement: 'campaign-selector',
        guideText: '👆 Clique aqui para trocar de campanha',
        guideAction: () => guideToElement('campaign-selector'),
      };
    }

    if (msg.includes('ranking') || msg.includes('posição') || msg.includes('classificação')) {
      return {
        id: Date.now().toString(),
        text:
          '🏆 **Ranking da Campanha**\n\nVou te mostrar onde ver seu ranking! 👇\n\n📊 Role a página para baixo e veja o **card de Ranking**!',
        sender: 'bot',
        timestamp,
        guideElement: 'ranking-card',
        guideText: '👇 Veja seu ranking aqui',
        guideAction: () => guideToElement('ranking-card'),
      };
    }

    // ... (outras respostas foram omitidas por brevidade)

    // RESPOSTAS GENÉRICAS
    if (msg.includes('obrigad') || msg.includes('valeu') || msg.includes('thanks')) {
      return {
        id: Date.now().toString(),
        text: '😊 Por nada! Estou sempre aqui para ajudar. Se tiver mais dúvidas, é só perguntar!',
        sender: 'bot',
        timestamp,
      };
    }

    if (msg.includes('oi') || msg.includes('olá') || msg.includes('ola') || msg.includes('hey')) {
      return {
        id: Date.now().toString(),
        text:
          '👋 Olá! Como posso ajudar você hoje? Posso responder sobre:\n\n• Campanhas e participação\n• Pontos e XP\n• Recompensas e resgates\n• Missões e desafios\n• Níveis e progresso\n• Distintivos\n• Jogos\n• Extrato\n• Perfil e configurações\n• Suporte\n\nO que você gostaria de saber?',
        sender: 'bot',
        timestamp,
      };
    }

    // PADRÃO
    return {
      id: Date.now().toString(),
      text:
        '🤔 Desculpe, não entendi sua pergunta.\n\n💡 **Posso te ajudar com:**\n• 📢 Campanhas e ranking\n• 💰 Pontos e XP\n• 🎁 Resgatar recompensas\n• 🎯 Missões e desafios\n• 📊 Níveis e progresso\n• 🏅 Distintivos\n• 🎮 Jogos\n• 📊 Extrato\n• 👤 Perfil\n• ⚙️ Configurações\n• 🆘 Suporte\n\n**Tente reformular sua pergunta ou escolha um tópico acima!** 😊',
      sender: 'bot',
      timestamp,
    };
  };

  const handleSendMessage = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;

    const userMsg: Message = {
      id: Date.now().toString(),
      text: trimmed,
      sender: 'user',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setIsTyping(true);
    setExpression('thinking');

    setTimeout(() => {
      const botResponse = getResponse(trimmed);
      setMessages((prev) => [...prev, botResponse]);
      setIsTyping(false);
      setExpression('talking');

      if (botResponse.guideElement) {
        setTimeout(() => {
          guideToElement(
            botResponse.guideElement!,
            botResponse.guideText,
            botResponse.guideAction,
          );
        }, 500);
      }

      setTimeout(() => setExpression('happy'), 2000);
    }, 1500);
  };

  const handleQuickQuestion = (question: QuickQuestion) => {
    setInputValue(question.text);
    handleSendMessage();
  };

  /* ---------------------------------------------------------------------- */
  /*                              TOUR LOGIC                                 */
  /* ---------------------------------------------------------------------- */
  const startTour = (tourType: 'pix' | 'pagamento') => {
    const steps = tourType === 'pix' ? pixTourSteps : pagamentoTourSteps;
    setCurrentTourSteps(steps);
    setCurrentTourStep(0);
    setCurrentTourType(tourType);
    setIsTourActive(true);
    setShowTourOverlay(true);
    setIsOpen(false);

    // Garantir que o modal (se houver) esteja fechado antes de destacar
    setTimeout(() => highlightElement(steps[0]), 300);
  };

  const highlightElement = (step: TourStep) => {
    const element = document.querySelector(step.target);
    if (!element) {
      console.warn(`Elemento não encontrado: ${step.target}`);
      // Tentativa de novo após pequeno delay
      setTimeout(() => {
        const retry = document.querySelector(step.target);
        if (retry) highlightElement(step);
      }, 500);
      return;
    }

    const rect = element.getBoundingClientRect();
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const scrollLeft = window.scrollX || document.documentElement.scrollLeft;

    // Ajuste para elementos dentro de modais scrolláveis
    let modalScrollTop = 0;
    let modalScrollLeft = 0;
    const modalContent = element.closest('.overflow-y-auto');
    if (modalContent) {
      modalScrollTop = (modalContent as HTMLElement).scrollTop;
      modalScrollLeft = (modalContent as HTMLElement).scrollLeft;
    }

    setHighlightPosition({
      top: rect.top + scrollTop - modalScrollTop,
      left: rect.left + scrollLeft - modalScrollLeft,
      width: rect.width,
      height: rect.height,
    });

    // Posicionamento do tooltip (simples: abaixo do elemento)
    setTooltipPosition({
      top: rect.bottom + scrollTop + 8,
      left: rect.left + scrollLeft,
    });

    element.scrollIntoView({ behavior: 'smooth', block: 'center' });

    if (step.action) {
      const clickHandler = () => {
        step.action?.();
        element.removeEventListener('click', clickHandler);
      };
      element.addEventListener('click', clickHandler);
    }
  };

  const nextTourStep = () => {
    if (currentTourStep < currentTourSteps.length - 1) {
      const next = currentTourStep + 1;
      setCurrentTourStep(next);
      setTimeout(() => highlightElement(currentTourSteps[next]), 300);
    } else {
      endTour();
    }
  };

  const prevTourStep = () => {
    if (currentTourStep > 0) {
      const prev = currentTourStep - 1;
      setCurrentTourStep(prev);
      setTimeout(() => highlightElement(currentTourSteps[prev]), 300);
    }
  };

  const endTour = () => {
    setIsTourActive(false);
    setShowTourOverlay(false);
    setHighlightPosition(null);
    setTooltipPosition(null);
    setCurrentTourStep(0);
    setCurrentTourSteps([]);
    setCurrentTourType(null);
  };

  /* ----------  REATUALIZAÇÃO DO DESTQUE EM RESIZE/SCROLL  ---------- */
  useEffect(() => {
    if (!isTourActive || !currentTourSteps[currentTourStep]) return;

    const updateHighlight = () => {
      const step = currentTourSteps[currentTourStep];
      if (!step || !step.target) return;

      const element = document.querySelector(step.target);
      if (!element) return;

      const rect = element.getBoundingClientRect();
      setHighlightPosition({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height,
      });
    };

    const handleResize = () => updateHighlight();
    const handleScroll = () => updateHighlight();

    updateHighlight();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [currentTourStep, isTourActive, currentTourSteps]);

  /* ----------  FECHAR CHAT AO CLICAR FORA  ---------- */
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  /* ---------------------------------------------------------------------- */
  /*                                 RENDER                                  */
  /* ---------------------------------------------------------------------- */
  return (
    <>
      {/* ---------- ESTILOS GLOBAIS ---------- */}
      <style>{`
        @keyframes chatbot-highlight-pulse {
          0%,100%{box-shadow:0 0 0 0 rgba(249,115,22,0.7);border-color:rgba(249,115,22,0.5)}
          50%{box-shadow:0 0 0 15px rgba(249,115,22,0);border-color:rgba(249,115,22,1)}
        }
        .chatbot-highlight{
          position:relative;
          animation:chatbot-highlight-pulse 2s infinite;
          border:3px solid #f97316!important;
          border-radius:12px!important;
          z-index:9998!important;
        }
        .chatbot-highlight::before{
          content:"";
          position:absolute;
          inset:-8px;
          border-radius:16px;
          background:linear-gradient(45deg,#f97316,#fb923c);
          opacity:0.1;
          z-index:-1;
        }
        .chatbot-guide-arrow{
          position:fixed;
          z-index:9999;
          animation:chatbot-arrow-bounce 1s infinite;
          pointer-events:none;
        }
        @keyframes chatbot-arrow-bounce{
          0%,100%{transform:translateY(0)}
          50%{transform:translateY(-10px)}
        }
        .chatbot-guide-text{
          position:fixed;
          z-index:9999;
          background:linear-gradient(135deg,#f97316 0%,#fb923c 100%);
          color:#fff;
          padding:12px 20px;
          border-radius:12px;
          font-weight:600;
          font-size:14px;
          box-shadow:0 8px 24px rgba(249,115,22,0.4);
          pointer-events:none;
          white-space:nowrap;
        }
        .chatbot-guide-text::after{
          content:"";
          position:absolute;
          width:0;
          height:0;
          border-style:solid;
        }
        .chatbot-guide-text.arrow-left::after{
          right:-10px;top:50%;transform:translateY(-50%);
          border-width:10px 0 10px 10px;
          border-color:transparent transparent transparent #fb923c;
        }
        .chatbot-guide-text.arrow-right::after{
          left:-10px;top:50%;transform:translateY(-50%);
          border-width:10px 10px 10px 0;
          border-color:transparent #fb923c transparent transparent;
        }
        .chatbot-guide-text.arrow-top::after{
          bottom:-10px;left:50%;transform:translateX(-50%);
          border-width:10px 10px 0 10px;
          border-color:#fb923c transparent transparent transparent;
        }
        .chatbot-guide-text.arrow-bottom::after{
          top:-10px;left:50%;transform:translateX(-50%);
          border-width:0 10px 10px 10px;
          border-color:transparent transparent #fb923c transparent;
        }
        @keyframes scale-in{
          from{opacity:0;transform:scale(0.9) translateY(20px)}
          to{opacity:1;transform:scale(1) translateY(0)}
        }
        .animate-scale-in{animation:scale-in .3s ease-out}
      `}</style>

      {/* ---------- GUIA VISUAL (seta + texto) ---------- */}
      {highlightedElement && (
        <>
          <div
            className="chatbot-guide-arrow"
            style={{
              top: '50%',
              right: '120px',
              fontSize: '48px',
              color: '#f97316',
            }}
          >
            👉
          </div>
          <div
            className="chatbot-guide-text arrow-right"
            style={{
              top: '50%',
              right: '180px',
              transform: 'translateY(-50%)',
            }}
          >
            Olhe aqui! 👀
          </div>
        </>
      )}

      {/* ---------- BOTÃO FLUTUANTE ---------- */}
      <div className="fixed right-6 bottom-32 md:bottom-6 z-50">
        <button
          onClick={() => setIsOpen((prev) => !prev)}
          className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-full shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-110 flex items-center justify-center group"
          aria-label="Abrir chat de suporte"
        >
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold animate-pulse">
            1
          </span>
          <div className="relative w-12 h-12">
            {/* Rosto */}
            <div className="absolute inset-0 bg-white rounded-full flex items-center justify-center">
              <div className="flex gap-2 mb-1">
                <div className={`w-2 h-2 bg-slate-800 rounded-full ${expression === 'thinking' ? 'animate-pulse' : ''}`} />
                <div className={`w-2 h-2 bg-slate-800 rounded-full ${expression === 'thinking' ? 'animate-pulse' : ''}`} />
              </div>
            </div>
            {/* Boca */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2">
              {expression === 'happy' && <div className="w-4 h-2 border-b-2 border-slate-800 rounded-full" />}
              {expression === 'thinking' && <div className="w-3 h-1 bg-slate-800 rounded-full" />}
              {expression === 'talking' && (
                <div className="w-3 h-3 border-2 border-slate-800 rounded-full animate-pulse" />
              )}
            </div>
          </div>
          <span className="absolute right-full mr-3 px-3 py-2 bg-slate-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
            Precisa de ajuda? Clique aqui!
          </span>
        </button>
      </div>

      {/* ---------- JANELA DE CHAT ---------- */}
      {isOpen && (
        <div
          className="fixed bottom-24 right-6 w-96 h-[600px] bg-white rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden animate-scale-in"
          ref={menuRef}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-red-600 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative w-10 h-10 bg-white rounded-full flex items-center justify-center">
                <div className="flex gap-1">
                  <div className="w-1.5 h-1.5 bg-slate-800 rounded-full" />
                  <div className="w-1.5 h-1.5 bg-slate-800 rounded-full" />
                </div>
                <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-3 h-1.5 border-b-2 border-slate-800 rounded-full" />
                <span className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 border-2 border-white rounded-full" />
              </div>
              <div>
                <h3 className="text-white font-bold">Assistente Virtual</h3>
                <p className="text-white/90 text-xs flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  Online agora
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/20 transition-colors"
              aria-label="Fechar chat"
            >
              <i className="ri-close-line text-white text-xl" />
            </button>
          </div>

          {/* Mensagens */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'} items-start gap-2`}
              >
                {msg.sender === 'bot' && (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm">🤖</span>
                  </div>
                )}
                <div className="flex flex-col max-w-[75%]">
                  <div
                    className={`rounded-2xl px-4 py-3 ${
                      msg.sender === 'user'
                        ? 'bg-gradient-to-br from-orange-500 to-orange-600 text-white'
                        : 'bg-slate-100 text-slate-800'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-line leading-relaxed">{msg.text}</p>
                  </div>

                  {msg.guideText && msg.guideAction && (
                    <button
                      onClick={msg.guideAction}
                      className="mt-2 flex items-center gap-2 text-xs text-white bg-gradient-to-r from-orange-500 to-orange-600 px-3 py-2 rounded-lg font-medium hover:shadow-lg hover:scale-105 transition-all"
                    >
                      <i className="ri-guide-line" />
                      <span>{msg.guideText}</span>
                    </button>
                  )}

                  <span className="text-xs text-slate-400 mt-1">
                    {msg.timestamp.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            ))}

            {/* Indicador de digitação */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white rounded-2xl rounded-bl-none px-4 py-3 shadow-sm">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            {/* Perguntas rápidas (exibidas apenas na primeira mensagem) */}
            {messages.length === 1 && (
              <div className="space-y-4">
                {/* Resgates Guiados */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 justify-center">
                    <i className="ri-hand-coin-line text-teal-600" />
                    <p className="text-xs text-slate-700 font-bold">Resgates Guiados</p>
                  </div>
                  <div className="grid gap-2">
                    <button
                      onClick={() => {
                        setIsOpen(false);
                        setTimeout(() => {
                          const event = new CustomEvent('startGuidedTour', {
                            detail: { type: 'pix' },
                          });
                          window.dispatchEvent(event);
                        }, 300);
                      }}
                      className="flex items-center gap-3 p-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl hover:shadow-lg hover:scale-105 transition-all group"
                    >
                      <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-money-dollar-circle-line text-2xl" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-bold text-sm">Resgate PIX</p>
                        <p className="text-xs text-white/90">Guia passo a passo</p>
                      </div>
                      <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform" />
                    </button>

                    <button
                      onClick={() => {
                        const userMsg: Message = {
                          id: Date.now().toString(),
                          text: 'Quero fazer um Pagamento de Contas',
                          sender: 'user',
                          timestamp: new Date(),
                        };
                        setMessages((prev) => [...prev, userMsg]);
                        setIsTyping(true);
                        setTimeout(() => {
                          const botMsg: Message = {
                            id: (Date.now() + 1).toString(),
                            text:
                              '🧾 **Guia: Pagamento de Contas**\n\nVou te guiar passo a passo para pagar suas contas! 👇\n\n**Passo 1:** Clique em "Recompensas" no menu lateral esquerdo 👈\n\n**Passo 2:** Na página de Recompensas, clique na categoria "Financeira" 💰\n\n**Passo 3:** Selecione a opção "Pagamento de Contas" 🧾\n\n**Passo 4:** Escolha qual crédito usar...\n\n**Passo 8:** Pronto! Sua conta será paga em até 24h úteis 🎉',
                            sender: 'bot',
                            timestamp: new Date(),
                          };
                          setMessages((prev) => [...prev, botMsg]);
                          setIsTyping(false);
                        }, 1500);
                      }}
                      className="flex items-center gap-3 p-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl hover:shadow-lg hover:scale-105 transition-all group"
                    >
                      <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-file-list-3-line text-2xl" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-bold text-sm">Pagamento de Contas</p>
                        <p className="text-xs text-white/90">Guia passo a passo</p>
                      </div>
                      <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>

                {/* Perguntas Rápidas */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 justify-center">
                    <i className="ri-question-line text-orange-600" />
                    <p className="text-xs text-slate-700 font-bold">Perguntas Rápidas</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {quickQuestions.map((q) => (
                      <button
                        key={q.id}
                        onClick={() => handleQuickQuestion(q)}
                        className="flex items-center gap-2 p-3 bg-white rounded-lg border border-slate-200 hover:border-orange-500 hover:shadow-md transition-all text-left group"
                      >
                        <i className={`${q.icon} text-orange-500 text-lg group-hover:scale-110 transition-transform`} />
                        <span className="text-xs text-slate-700 font-medium">{q.text}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 bg-white border-t border-slate-200">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage();
              }}
              className="flex gap-2"
            >
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Digite sua mensagem..."
                className="flex-1 px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
              />
              <button
                type="submit"
                disabled={!inputValue.trim()}
                className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                aria-label="Enviar mensagem"
              >
                <i className="ri-send-plane-fill text-lg" />
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ---------- OVERLAY DO TOUR ---------- */}
      {showTourOverlay && currentTourType && (
        <div className="fixed inset-0 bg-black/50 z-[9998]">
          {/* Destaque */}
          {highlightPosition && (
            <div
              className="absolute border-4 border-orange-500 rounded-lg pointer-events-none transition-all duration-300"
              style={{
                top: `${highlightPosition.top}px`,
                left: `${highlightPosition.left}px`,
                width: `${highlightPosition.width}px`,
                height: `${highlightPosition.height}px`,
              }}
            />
          )}

          {/* Tooltip */}
          {tooltipPosition && (
            <div
              className="absolute bg-white rounded-lg shadow-xl p-4 max-w-sm z-[9999]"
              style={{
                top: `${tooltipPosition.top}px`,
                left: `${tooltipPosition.left}px`,
              }}
            >
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold text-gray-900">
                  {currentTourType === 'pix'
                    ? pixTourSteps[currentTourStep]?.title
                    : pagamentoTourSteps[currentTourStep]?.title}
                </h3>
                <button onClick={endTour} className="text-gray-400 hover:text-gray-600">
                  <i className="ri-close-line text-xl" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                {currentTourType === 'pix'
                  ? pixTourSteps[currentTourStep]?.description
                  : pagamentoTourSteps[currentTourStep]?.description}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  Passo {currentTourStep + 1} de{' '}
                  {currentTourType === 'pix' ? pixTourSteps.length : pagamentoTourSteps.length}
                </span>
                <div className="flex gap-2">
                  {currentTourStep > 0 && (
                    <button
                      onClick={() => {
                        const prev = currentTourStep - 1;
                        setCurrentTourStep(prev);
                        highlightElement(
                          currentTourType === 'pix' ? pixTourSteps[prev] : pagamentoTourSteps[prev],
                        );
                      }}
                      className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
                    >
                      Voltar
                    </button>
                  )}
                  <button
                    onClick={nextTourStep}
                    className="px-4 py-1 bg-orange-500 text-white rounded-lg text-sm hover:bg-orange-600"
                  >
                    {currentTourStep ===
                    (currentTourType === 'pix' ? pixTourSteps.length : pagamentoTourSteps.length) - 1
                      ? 'Finalizar'
                      : 'Próximo'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ChatbotAvatar;
